import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "api/apiClient.ts";

// Type definitions
export interface ContributingProduct {
  orderedProductId: string;
  orderedProductName: Record<"ar" | "en", string>;
  orderQuantity: number;
  packQuantity: number;
  supplyQuantity: number;
}

export interface ProductProgress {
  packProductId: string;
  packProductName: Record<"ar" | "en", string>;
  categoryId: string;
  minSupplyQuantity: number | null | undefined;
  accumulatedQuantity: number;
  contributingProducts: ContributingProduct[];
  progress: number;
}

export interface CategoryProgress {
  categoryId: string;
  categoryName: Record<"ar" | "en", string>;
  minSupplyQuantity: number | null | undefined;
  accumulatedQuantity: number;
  progress: number;
}

export interface PendingOrder {
  orderId: string;
  quantity: number;
  packQuantity: number;
  supplyQuantity: number;
  orderedProductId: string;
  orderedProductName: string;
  mosque: {
    mosqueId: string;
    mosqueName: string;
    responsibleName: string;
    mobileNumber: string;
    secondNumber?: string;
    address: string;
    city: string;
    district?: string;
    postalCode?: string;
    location: { type: "Point"; coordinates: [number, number] };
  };
}

export interface PendingSupplyItem {
  productId: string;
  productName: string;
  totalQuantity: number;
  orderCount: number;
  orders: PendingOrder[];
}

export interface MarkSuppliedRequest {
  orderId: string;
  productIds?: string[];
}

// Progress API hooks
const fetchProgress = async (
  scope: "product" | "category",
  packProductId?: string,
  categoryId?: string
): Promise<ProductProgress[] | CategoryProgress[]> => {
  const params = new URLSearchParams({
    scope,
    ...(packProductId && { packProductId }),
    ...(categoryId && { categoryId }),
  });

  const { data } = await apiClient.get(`/supply/progress?${params.toString()}`);

  if (data.status !== "success" || !Array.isArray(data.data)) {
    throw new Error("Invalid response format");
  }

  // Validate and sanitize the data structure for product scope
  if (scope === "product") {
    const validatedData = data.data.map((item: any) => {
      // Validate required fields
      if (!item.packProductId || !item.packProductName) {
        console.warn("Invalid product data: missing packProductId or packProductName", item);
        return {
          ...item,
          packProductId: item.packProductId || "unknown",
          packProductName: item.packProductName || { en: "Unknown Product", ar: "منتج غير معروف" },
          contributingProducts: Array.isArray(item.contributingProducts)
            ? item.contributingProducts
            : [],
        };
      }

      // Validate contributingProducts array
      const contributingProducts = Array.isArray(item.contributingProducts)
        ? item.contributingProducts.filter((cp: any) => {
            if (!cp.orderedProductId || !cp.orderedProductName) {
              console.warn("Invalid contributing product: missing required fields", cp);
              return false;
            }
            return true;
          })
        : [];

      return {
        ...item,
        contributingProducts,
      };
    });

    return validatedData;
  }

  return data.data;
};

export const useSupplyProgress = (
  scope: "product" | "category",
  packProductId?: string,
  categoryId?: string
) => {
  return useQuery({
    queryKey: ["supply-progress", scope, packProductId, categoryId],
    queryFn: () => fetchProgress(scope, packProductId, categoryId),
    staleTime: 30000, // 30 seconds
  });
};

// Pending requests API hooks
const fetchPendingRequests = async (): Promise<PendingSupplyItem[]> => {
  const { data } = await apiClient.get("/supply/pending");

  if (data.status !== "success" || !Array.isArray(data.data)) {
    throw new Error("Invalid response format");
  }

  // Validate and sanitize the data structure
  const validatedData = data.data.map((item: any) => {
    return {
      productId: item.productId || "unknown",
      productName: item.productName || "Unknown Product",
      totalQuantity: item.totalQuantity || 0,
      orderCount: item.orderCount || 0,
      orders: Array.isArray(item.orders) ? item.orders : [],
    };
  });

  return validatedData;
};

export const usePendingSupply = () => {
  return useQuery({
    queryKey: ["supply-pending"],
    queryFn: fetchPendingRequests,
    staleTime: 60000, // 1 minute
    refetchInterval: 60000, // Auto-refetch every minute
  });
};

// Flattened pending supply data for fulfillment tab
const fetchPendingSupplyForFulfillment = async (): Promise<any[]> => {
  const { data } = await apiClient.get("/supply/pending");

  if (data.status !== "success" || !Array.isArray(data.data)) {
    throw new Error("Invalid response format");
  }

  // Flatten the grouped data into individual order items
  const flattenedData: any[] = [];

  data.data.forEach((productGroup: any) => {
    if (Array.isArray(productGroup.orders)) {
      productGroup.orders.forEach((order: any) => {
        flattenedData.push({
          orderId: order.orderId,
          productId: productGroup.productId,
          productName: productGroup.productName,
          quantity: order.quantity,
          mosqueName: order.mosque?.mosqueName || "Unknown Mosque",
          responsibleName: order.mosque?.responsibleName || "Unknown",
          mobileNumber: order.mosque?.mobileNumber || "",
          address: order.mosque?.address || "",
        });
      });
    }
  });

  return flattenedData;
};

export const usePendingSupplyForFulfillment = () => {
  return useQuery({
    queryKey: ["supply-pending-fulfillment"],
    queryFn: fetchPendingSupplyForFulfillment,
    staleTime: 60000, // 1 minute
    refetchInterval: 60000, // Auto-refetch every minute
  });
};

// Mark as supplied API hooks
export const useMarkSupplied = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: MarkSuppliedRequest) => {
      const { data } = await apiClient.post("/supply/mark-supplied", request);
      return data;
    },
    onSuccess: () => {
      // Invalidate both progress and pending queries
      queryClient.invalidateQueries({ queryKey: ["supply-progress"] });
      queryClient.invalidateQueries({ queryKey: ["supply-pending"] });
      queryClient.invalidateQueries({ queryKey: ["supply-pending-fulfillment"] });
    },
  });
};

// Refetch hooks for manual refresh
export const useRefetchSupplyProgress = () => {
  const queryClient = useQueryClient();

  return {
    refetch: async (scope?: "product" | "category") => {
      if (scope) {
        await queryClient.invalidateQueries({
          queryKey: ["supply-progress", scope],
        });
      } else {
        await queryClient.invalidateQueries({
          queryKey: ["supply-progress"],
        });
      }
    },
  };
};

export const useRefetchPendingSupply = () => {
  const queryClient = useQueryClient();

  return {
    refetch: async () => {
      await queryClient.invalidateQueries({ queryKey: ["supply-pending"] });
    },
  };
};
